import { type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";
import type { ResellerBranding } from "@/utils/reseller/loginBranding";

export interface ResellerLogoProps {
  branding: ResellerBranding;
  /** The <768px top-band layout, as for the product logo. */
  compact: boolean;
  /** The product's own logo, shown when the reseller's cannot be. */
  fallback: ReactNode;
}

/**
 * A reseller's logo in the brand panel's logo slot, at the product logo's
 * height (28px, 22px compact; sized in base.css). The full logo when there is
 * one, else the isotype. With neither, or when the image fails to load, the
 * product's own logo shows instead, so the panel is never left empty.
 */
export function ResellerLogo({
  branding,
  compact,
  fallback,
}: ResellerLogoProps) {
  const src = branding.logoUrl ?? branding.isotypeUrl;
  // Remember which URL failed, so a new URL gets its own attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!src || src === failedSrc) return <>{fallback}</>;

  return (
    <img
      className={cn("bs-reseller-logo", compact && "bs-reseller-logo--compact")}
      src={src}
      alt={branding.displayName}
      height={compact ? 22 : 28}
      onError={() => setFailedSrc(src)}
    />
  );
}
